import { memo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import { useWishlist } from "../../contexts/WishlistContext";
import PriceDisplay from "../shared/PriceDisplay";
import { withViewTransition } from "../../utils/viewTransition";
import { setMorphCandidate, morphNameFor } from "../../utils/productMorph";
import { productQueryOptions } from "../../api/products";
import { useProductViewer } from "../../hooks/useProductViewer";
import { useWishPop } from "../../hooks/useWishPop";
import { trackSearchClick, trackRailImpression, trackRailClick } from "../../utils/analytics";
import { useViewability } from "../../utils/useViewability";
import "./ProductCard.css";
import { imageProps, SIZES } from "../../utils/imageUrl";
import { lowStockCount } from "../../utils/stock";
import { productShopPath } from "../../utils/shopPath";
import { displayShopName } from "../../utils/shopName";
import VerifiedBadge from "../shared/VerifiedBadge";

const RESTOCK_WINDOW_DAYS = 14;
const PREFETCH_DEBOUNCE_MS = 80;

// Compact-only display shortening (Home.jsx "Picked for you"). Drops a
// trailing spec-dump in parentheses (e.g. "(6.7" Super Retina XDR, A17 Pro
// Chip, 8GB RAM)") and a leading brand word only when it's redundant once
// the model name itself makes the brand obvious ("Apple iPhone..." reads
// fine as just "iPhone..."; "Beni Smart Camera Robot" keeps "Beni" since
// it isn't a self-evident brand). Truncates at a word boundary, never
// mid-word. This only ever changes what's SHOWN — every aria-label/alt
// below still uses the full, untouched `name` for accessibility.
const COMPACT_NAME_MAX_LENGTH = 20;
const REDUNDANT_LEADING_BRANDS = ["Apple"];

const compactTitle = (fullName) => {
  const base = fullName.split(" (")[0].trim();
  const [firstWord, ...rest] = base.split(" ");
  const shortened = REDUNDANT_LEADING_BRANDS.includes(firstWord) && rest.length
    ? rest.join(" ")
    : base;
  if (shortened.length <= COMPACT_NAME_MAX_LENGTH) return shortened;
  const cut = shortened.slice(0, COMPACT_NAME_MAX_LENGTH);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim()}...`;
};

// Non-compact card title shortening — word-count based (unlike
// compactTitle's char-length rule above), and a separate concern: every
// full-size card (search results, category grids, rails) gets this
// instead of the raw name. Rebuilds the string from the split words
// rather than slicing the original, so repeated internal whitespace
// gets normalized too. Exactly NAME_MAX_WORDS words is left unchanged,
// with no trailing "...".
const NAME_MAX_WORDS = 4;
const truncateName = (fullName) => {
  const words = (fullName || "").trim().split(/\s+/).filter(Boolean);
  return words.length <= NAME_MAX_WORDS ? (fullName || "") : `${words.slice(0, NAME_MAX_WORDS).join(" ")}...`;
};

const TAG_STYLES = {
  New:              "pc-tag-new",
  Bestseller:       "pc-tag-best",
  Sale:             "pc-tag-sale",
  "Hot Deal":       "pc-tag-hot",
  "Limited Edition":"pc-tag-limited",
  Trending:         "pc-tag-trending",
  Clearance:        "pc-tag-clearance",
  Featured:         "pc-tag-featured",
  "Pre-Order":      "pc-tag-preorder",
};

const ProductCard = ({
  product, dark = false,
  // Opt-in, defaults false everywhere so every other rail/category/
  // search-result card is unaffected. Shortens
  // a spec-dump title — used by the homepage's "Picked for you" section,
  // which only needs image, 2-line title, price, and status badges.
  compact = false,
  // Emporia Brain P1.2 — set only when this card is rendered inside a
  // text-search results grid (see SearchPage.jsx). Absent (null) for every
  // other rail/category/collection usage, so trackSearchClick below is a
  // guaranteed no-op there.
  searchRequestId = null, searchPosition = null, searchResultCount = null,
  // Emporia Brain P1.3 — set only when rendered inside a ProductRail
  // (railKey = the rail's slug, railPosition = index within that rail).
  railKey = null, railPosition = null,
  // "Sold by <shop>" under the name, linking to the shop page. Off on the
  // shop page itself (every card there is the same shop) and on compact cards.
  showSeller = true,
}) => {
  const { id, name, price, tag, image_url, restocked_at, is_preorder, slug, compare_at_price } = product;
  const productPath = `/product/${slug ?? id}`;
  const { isWishlisted, toggleWishlist } = useWishlist();
  const navigate = useNavigate();
  const imgRef = useRef(null);
  const cardRef = useRef(null);
  const queryClient = useQueryClient();
  const viewer = useProductViewer();
  const prefetchTimerRef = useRef(null);
  const prefetchedRef = useRef(false);

  // Warms the same react-query cache entry ProductDetailPage reads from
  // (productQueryOptions — per-account key and fetch, see
  // src/api/products.js) and the browser's own image cache, so the
  // destination has nothing left to wait on by the time the click lands.
  // Slight debounce so a quick mouse pass-over across a grid doesn't fire a
  // prefetch per card; only once per card instance (repeat hovers are then
  // free — react-query's own staleTime covers that too, prefetchedRef just
  // skips the redundant call). Skipped until auth resolves, like the page.
  const intentPrefetch = () => {
    if (prefetchedRef.current || !viewer.ready) return;
    prefetchTimerRef.current = setTimeout(() => {
      prefetchedRef.current = true;
      queryClient.prefetchQuery(productQueryOptions(slug ?? id, viewer));
      if (image_url) new Image().src = image_url;
    }, PREFETCH_DEBOUNCE_MS);
  };
  const cancelIntentPrefetch = () => {
    if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
  };

  const trackIfSearchResult = () => {
    if (searchRequestId) trackSearchClick(searchRequestId, id, searchPosition, searchResultCount);
  };
  const trackIfRailItem = () => {
    if (railKey) trackRailClick(railKey, id, railPosition);
  };

  // Emporia Brain P1.3 — fires a viewable rail_impression at most once per
  // mount, only once this card has genuinely stayed in view (not merely
  // rendered). No-ops when railKey isn't set (every non-rail usage).
  useViewability(cardRef, () => {
    if (railKey) trackRailImpression(railKey, id, railPosition);
  });

  // A normal left-click gets the shared-element morph + a transitioned
  // navigation; modified clicks (cmd/ctrl/shift/middle-click) must fall
  // through to the browser's native "open in new tab" behavior untouched.
  const handleImageClick = (e) => {
    trackIfSearchResult();
    trackIfRailItem();
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    // Only the clicked card's image ever carries a viewTransitionName —
    // every other card has none, so there's never a duplicate-name clash.
    // Set via direct DOM mutation (not React state) so it's in effect
    // synchronously before the transition starts, with no extra render.
    if (imgRef.current) imgRef.current.style.viewTransitionName = morphNameFor(id);
    setMorphCandidate(id);
    withViewTransition(() => navigate(productPath));
  };

  const handleNavLinkClick = (e) => {
    trackIfSearchResult();
    trackIfRailItem();
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    withViewTransition(() => navigate(productPath));
  };

  // "Only N left" only when the stock is real (utils/stock.js).
  const lowLeft = lowStockCount(product);
  const lowStock = lowLeft != null;
  const backInStock = !lowStock && restocked_at &&
    (Date.now() - new Date(restocked_at).getTime()) / 86400000 <= RESTOCK_WINDOW_DAYS;

  const preorderBadge = is_preorder ? { cls: "pc-tag-preorder", label: "Pre-Order" } : null;
  const tagBadge       = tag && TAG_STYLES[tag] && TAG_STYLES[tag] !== preorderBadge?.cls
    ? { cls: TAG_STYLES[tag], label: tag } : null;
  const statusBadge    = lowStock
    ? { cls: "pc-tag-lowstock", label: `Only ${lowLeft} left` }
    : backInStock
      ? { cls: "pc-tag-restock", label: "Back in Stock" }
      : null;
  const badges = [preorderBadge, tagBadge, statusBadge].filter(Boolean).slice(0, 2);

  // Only for a seller with a handle: the line is a link to the shop, and a
  // shop without a handle has no page. Never the legal name: the API sends
  // only seller_display_name / seller_shop_name (app/shop_public.py).
  const sellerPath = showSeller && !compact ? productShopPath(product) : null;
  const sellerLabel = sellerPath
    ? (product.seller_display_name || displayShopName(product.seller_shop_name))
    : null;

  const wishlisted = isWishlisted(id);
  const [popping, pop] = useWishPop();

  return (
    <div
      ref={cardRef}
      className={`pc-card${dark ? " pc-dark" : ""}${compact ? " pc-compact" : ""}`}
      onMouseEnter={intentPrefetch}
      onMouseLeave={cancelIntentPrefetch}
      onTouchStart={intentPrefetch}
    >
      <div className="pc-media">
        <Link to={productPath} className="pc-img-link" aria-label={name} onClick={handleImageClick}>
          {image_url
            ? <img ref={imgRef} className="pc-image" {...imageProps(image_url, "product", SIZES.card)} alt={name} loading="lazy" decoding="async" style={{ contain: "paint" }} />
            : <div className="pc-image pc-image-placeholder" aria-hidden="true" />}
        </Link>

        <div className="pc-tags">
          {badges.map((b) => (
            <span key={b.label} className={`pc-tag ${b.cls}`}>{b.label}</span>
          ))}
        </div>

        <button
          type="button"
          className={`pc-wishlist-btn${wishlisted ? " active" : ""}${popping ? " is-popping" : ""}`}
          aria-pressed={wishlisted}
          aria-label={wishlisted ? `Remove ${name} from wishlist` : `Add ${name} to wishlist`}
          onClick={() => {
            if (!wishlisted) pop();   // only on the way in — see useWishPop
            toggleWishlist(product);
          }}
        >
          {wishlisted ? <AiFillHeart /> : <AiOutlineHeart />}
        </button>
      </div>

      <div className="pc-info">
        <Link to={productPath} className="pc-name" onClick={handleNavLinkClick}>{compact ? compactTitle(name) : truncateName(name)}</Link>

        {sellerLabel && (
          <Link to={sellerPath} className="pc-seller">
            <span className="pc-seller-prefix">Sold by </span>
            <span className="pc-seller-name">{sellerLabel}</span>
            {product.seller_verified && <VerifiedBadge />}
          </Link>
        )}

        <div className="pc-divider" />

        {/* No "Preview" button (removed for good, 2026-10-11): it only linked
            to the product page, which the image and the name already open. */}
        <div className="pc-footer">
          <PriceDisplay price={price} compareAtPrice={compare_at_price} size="sm" />
        </div>
      </div>
    </div>
  );
};

// Memoised (speed brief item 26): a grid or rail redraws when the cart or a
// filter changes, and a card whose product and handlers are unchanged has
// nothing new to draw.
export default memo(ProductCard);
